import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { T } from "@/content/site";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Collection() {
  const { t } = useLang();
  const products = T.collection.products;
  const [openSlug, setOpenSlug] = useState(null);
  const openProduct = products.find((p) => p.slug === openSlug);

  return (
    <section id="collection" data-testid="collection-section" className="relative bg-[#0F0F0F] py-[140px] md:py-[200px]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 mb-20 md:mb-28">
          <div className="col-span-12 md:col-span-5">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline mb-8">
              <span className="inline-block w-8 h-px bg-[color:var(--sacrvm-gold)] align-middle mr-3" />
              {t(T.collection.overline)}
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="font-serif text-white text-[48px] md:text-[80px] leading-[1] tracking-[-0.01em]"
            >
              {t(T.collection.title)}
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 flex items-end">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 1.2 }}
              className="text-[color:var(--sacrvm-text-muted)] text-[17px] md:text-[19px] font-serif italic leading-[1.5] max-w-[520px]"
            >
              {t(T.collection.subtitle)}
            </motion.p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-14">
          {products.map((p, i) => {
            const spans = [
              "col-span-12 md:col-span-7 md:row-span-1",
              "col-span-12 md:col-span-5",
              "col-span-12 md:col-span-5",
              "col-span-12 md:col-span-7",
            ];
            return (
              <motion.article
                key={p.slug}
                data-testid={`product-card-${p.slug}`}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, delay: i * 0.08, ease: [0.19, 1, 0.22, 1] }}
                className={"group " + spans[i % spans.length]}
              >
                <div
                  className="img-hover relative aspect-[4/5] md:aspect-[3/4] overflow-hidden"
                  style={{ background: p.bg || "#141414" }}
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className={"w-full h-full " + (p.fit === "contain" ? "object-contain p-6 md:p-10" : "object-cover")}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent pointer-events-none" />
                  <div className="absolute top-6 left-6 overline !text-white/70">Nº 0{i + 1}</div>
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                    <div>
                      <div className="font-serif text-white text-[26px] md:text-[36px] leading-tight">{p.name}</div>
                      <div className="mt-2 text-white/70 text-[12px] tracking-[0.22em] uppercase">{p.abv}</div>
                    </div>
                    <button
                      data-testid={`product-open-${p.slug}`}
                      onClick={() => setOpenSlug(p.slug)}
                      className="w-11 h-11 border border-white/60 text-white flex items-center justify-center hover:bg-[color:var(--sacrvm-gold)] hover:text-black hover:border-[color:var(--sacrvm-gold)] transition-all duration-500"
                      aria-label={`Open ${p.name}`}
                    >
                      <ArrowUpRight size={18} />
                    </button>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-12 gap-4">
                  <div className="col-span-12 md:col-span-6">
                    <div className="overline !text-white/50 mb-2">Agave</div>
                    <div className="font-serif text-white text-[18px]">{t(p.agave)}</div>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <p className="text-[color:var(--sacrvm-text-muted)] text-[15px] leading-[1.65]">{t(p.desc)}</p>
                    <button
                      data-testid={`product-cta-${p.slug}`}
                      onClick={() => setOpenSlug(p.slug)}
                      className="link-underline mt-4 text-[color:var(--sacrvm-gold)]"
                    >
                      {t(T.collection.cta)}
                    </button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      <Dialog open={!!openSlug} onOpenChange={(o) => !o && setOpenSlug(null)}>
        <DialogContent
          data-testid="product-inquiry-modal"
          className="bg-[#0F0F0F] border border-white/10 text-white max-w-[720px] p-0"
        >
          {openProduct && <ProductInquiryBody product={openProduct} onClose={() => setOpenSlug(null)} />}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ProductInquiryBody({ product, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/product-inquiry", { product_slug: product.slug, ...form });
      toast.success(t({ es: "Solicitud enviada. Gracias.", en: "Request sent. Thank you." }));
      onClose();
    } catch (err) {
      toast.error(t({ es: "No pudimos enviar la solicitud.", en: "We couldn't send the request." }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
      <div className="hidden md:block relative aspect-square">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
      </div>
      <div className="p-8 md:p-10 relative">
        <button
          data-testid="modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white p-2"
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <DialogTitle className="sr-only">{product.name}</DialogTitle>
        <div className="overline mb-3">{t({ es: "Solicitar información", en: "Request information" })}</div>
        <h3 className="font-serif text-white text-[30px] leading-tight mb-2">{product.name}</h3>
        <p className="text-white/60 text-[13px] mb-6">{t(product.agave)} · {product.abv}</p>

        <form onSubmit={submit} className="flex flex-col gap-4" data-testid="product-inquiry-form">
          <div className={"field " + (form.name ? "filled" : "")}>
            <label>{t({ es: "Nombre", en: "Name" })}</label>
            <input data-testid="inq-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className={"field " + (form.email ? "filled" : "")}>
            <label>{t({ es: "Correo", en: "Email" })}</label>
            <input data-testid="inq-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </div>
          <div className={"field " + (form.whatsapp ? "filled" : "")}>
            <label>WhatsApp</label>
            <input data-testid="inq-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
          </div>
          <div className={"field " + (form.message ? "filled" : "")}>
            <label>{t({ es: "Mensaje", en: "Message" })}</label>
            <textarea data-testid="inq-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <button data-testid="inq-submit" disabled={loading} className="btn-sharp gold mt-4 self-start">
            {loading ? t({ es: "Enviando…", en: "Sending…" }) : t({ es: "Enviar", en: "Send" })}
          </button>
        </form>
      </div>
    </div>
  );
}
